import { t, Trans } from '@lingui/macro';
import React, { useEffect, useMemo, useState } from 'react';

import { keyPressHelper } from '@brandfolder/utilities';
import { CommonFaceResponse, getCommonFaces, splitIdentity } from '@api/v4/private/people_tagging/faces';
import { Locale } from '@components/common/language_menu/languagesMap';
import { BulkCustomFieldsList } from '@components/show_page/bulk_actions/sub_components/BulkCustomFieldsList';
import BulkPeopleContent from '@components/show_page/bulk_actions/sub_components/BulkPeopleContent';
import TagsModalContent from '@components/show_page/bulk_actions/sub_components/TagsModalContent';
import { unsavedChangesOptions } from '@helpers/sweet_alert_options';

import renderModal from '../modals/renderModal';

enum TabType {
  Tags = 'tags',
  People = 'people',
  CustomFields = 'customFields',
}

interface BulkTagWrapperProps {
  closeModal: () => void;
  selectedAssetKeys: Set<string>;
  ugtLocale?: Locale;
}

const BulkTagWrapper: React.FC<BulkTagWrapperProps> = ({
  closeModal,
  selectedAssetKeys,
  ugtLocale,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(TabType.Tags);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removedFaceIds, setRemovedFaceIds] = useState<Set<string>>(new Set());
  const [facesToSplit, setFacesToSplit] = useState<string[][]>([]);
  const [isLoadingFaces, setIsLoadingFaces] = useState(false);
  const [faces, setFaces] = useState<CommonFaceResponse[]>([]);
  const [hasFetchedFaces, setHasFetchedFaces] = useState(false);

  useEffect(() => {
    // Track Bulk Tag modal page load with Amplitude
    if (window.BFG?.amplitude?.enabled && window.amplitudeService) {
      window.amplitudeService.trackEvent('PageLoad', {
        elementName: 'BulkTagView',
        elementLocation: 'BulkTagModal',
        productArea: 'BulkActionBar',
        featureArea: 'BulkTagModal',
        eventName: 'PageLoad',
        'key properties': {
          assetIds: Array.from(selectedAssetKeys || [])
        }
      });
    }

    return () => { closeModal(); };
  }, [closeModal, selectedAssetKeys]);

  // Fetch faces when People tab is activated
  useEffect(() => {
    if (activeTab === TabType.People && !hasFetchedFaces) {
      setIsLoadingFaces(true);
      getCommonFaces({
        brandfolderKey: BFG.resource.key,
        assetKeys: Array.from(selectedAssetKeys),
      })
        .then((data) => {
          setFaces(data);
          setHasFetchedFaces(true);
        })
        .catch((error) => {
          console.error('Error fetching faces:', error);
          Notify.create({
            title: t`Failed to load faces. Please try again.`,
            type: 'error',
          });
        })
        .finally(() => {
          setIsLoadingFaces(false);
        });
    }
  }, [activeTab, hasFetchedFaces, selectedAssetKeys]);

  const toggleTab = (tab: TabType): void => {
    if (hasUnsavedChanges) {
      window.swal(unsavedChangesOptions({
        onLeaveCopy: t`Discard Changes`,
        onStayCopy: t`Continue Editing`,
      }), (confirm: boolean) => {
        if (confirm) {
          setActiveTab(tab);
          setHasUnsavedChanges(false);
          setRemovedFaceIds(new Set());
          setFacesToSplit([]);
        }
      });
    } else {
      setActiveTab(tab);
      setHasUnsavedChanges(false);
    }
  };

  const handleClose = (): void => {
    if (hasUnsavedChanges) {
      window.swal(unsavedChangesOptions({
        onStayCopy: t`Continue Editing`,
      }), (confirm: boolean) => {
        if (confirm) {
          closeModal();
        }
      });
    } else {
      closeModal();
    }
  };

  const handleRemoveFace = (faceIds: string[]): void => {
    setRemovedFaceIds((prev) => {
      const updated = new Set(prev);
      faceIds.forEach((id) => updated.add(id));
      return updated;
    });
    setFacesToSplit((prev) => {
      // Check if this face group is already in the list
      const faceIdsStr = faceIds.sort().join(',');
      const alreadyExists = prev.some(
        (existingFaceIds) => existingFaceIds.sort().join(',') === faceIdsStr
      );
      if (alreadyExists) {
        return prev;
      }
      return [...prev, faceIds];
    });
    setHasUnsavedChanges(true);
  };

  const handleSave = async (): Promise<void> => {
    setIsSubmitting(true);

    try {
      // Make a separate API call for each face group to split (remove identity from)
      const promises = facesToSplit.map((faceIds) =>
        splitIdentity({
          brandfolderKey: BFG.resource.key,
          faceIds,
        })
      );

      await Promise.all(promises);

      Notify.create({
        title: t`Processing people tag updates!`,
        body: t`Updates will be available shortly.`,
        type: 'success',
      });
      // Remove the deleted faces from state so they don't reappear
      setFaces((prevFaces) => prevFaces.filter(
        (face) => !face.face_ids.some((id) => removedFaceIds.has(id))
      ));
      setHasUnsavedChanges(false);
      setRemovedFaceIds(new Set());
      setFacesToSplit([]);
    } catch (error) {
      console.error('Error removing people tags:', error);
      Notify.create({
        title: t`Failed to remove people tags. Please try again.`,
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasCustomFields = BFG.context.hasFeature('custom_fields');
  const assetCount = selectedAssetKeys.size;

  // Memoize visible faces to avoid recalculating on every render
  const visibleFaces = useMemo(() => (
    faces.filter((face) => !face.face_ids.some((id) => removedFaceIds.has(id)))
  ), [faces, removedFaceIds]);

  const peopleTabId = 'bulk-tag-people-tab';
  const tagsTabId = 'bulk-tag-tags-tab';
  const customFieldsTabId = 'bulk-tag-custom-fields-tab';
  const peopleTabPanelId = 'bulk-tag-people-tabpanel';
  const tagsTabPanelId = 'bulk-tag-tags-tabpanel';
  const customFieldsTabPanelId = 'bulk-tag-custom-fields-tabpanel';

  return (
    <div className="bulk-tag-wrapper">
      <div
        aria-label={t`Bulk tag options`}
        className="modal-content-wrapper__header wrapper-headers"
        role="tablist"
      >
        {BFG.context.hasFeature('people_tagging') && BFG.brandfolderSettings.people_tagging && (
          <div
            aria-controls={peopleTabPanelId}
            aria-selected={activeTab === TabType.People}
            className={`header ${activeTab === TabType.People ? 'active' : ''}`}
            id={peopleTabId}
            onClick={() => toggleTab(TabType.People)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleTab(TabType.People);
              }
            }}
            role="tab"
            tabIndex={activeTab === TabType.People ? 0 : -1}
          >
            <span aria-hidden="true" className="bff-people icon" />
            <h3 className="modal-title"><Trans>People</Trans></h3>
          </div>
        )}
        <div
          aria-controls={tagsTabPanelId}
          aria-selected={activeTab === TabType.Tags}
          className={`header ${activeTab === TabType.Tags ? 'active' : ''} ${!hasCustomFields ? 'one-tab' : ''}`}
          id={tagsTabId}
          onClick={() => toggleTab(TabType.Tags)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleTab(TabType.Tags);
            }
          }}
          role="tab"
          tabIndex={activeTab === TabType.Tags ? 0 : -1}
        >
          <span aria-hidden="true" className="bff-tag icon" />
          <h3 className="modal-title"><Trans>Bulk Tag</Trans></h3>
        </div>
        {hasCustomFields && (
          <div
            aria-controls={customFieldsTabPanelId}
            aria-selected={activeTab === TabType.CustomFields}
            className={`header ${activeTab === TabType.CustomFields ? 'active' : ''}`}
            id={customFieldsTabId}
            onClick={() => {
              // Track Bulk Custom Fields tab select with Amplitude
              if (window.BFG?.amplitude?.enabled && window.amplitudeService) {
                window.amplitudeService.trackEvent('TabSelect', {
                  elementName: 'BulkCustomFieldsTab',
                  elementLocation: 'BulkTagModal',
                  productArea: 'BulkActionBar',
                  featureArea: 'BulkTagModal',
                  eventName: 'TabSelect',
                  'key properties': {
                    assetIds: Array.from(selectedAssetKeys || [])
                  }
                });
              }
              toggleTab(TabType.CustomFields);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleTab(TabType.CustomFields);
              }
            }}
            role="tab"
            tabIndex={activeTab === TabType.CustomFields ? 0 : -1}
          >
            <span aria-hidden="true" className="bff-custom-fields icon" />
            <h3 className="modal-title"><Trans>Bulk Custom Fields</Trans></h3>
          </div>
        )}
      </div>
      {activeTab === TabType.People && BFG.context.hasFeature('people_tagging') ? (
        <div
          aria-labelledby={peopleTabId}
          id={peopleTabPanelId}
          role="tabpanel"
        >
          <BulkPeopleContent
            assetCount={assetCount}
            handleClose={handleClose}
            handleRemoveFace={handleRemoveFace}
            handleSave={handleSave}
            isLoadingFaces={isLoadingFaces}
            isSubmitting={isSubmitting}
            removedFaceIdsCount={removedFaceIds.size}
            visibleFaces={visibleFaces}
          />
        </div>
      ) : activeTab === TabType.CustomFields ? (
        <div
          aria-labelledby={customFieldsTabId}
          id={customFieldsTabPanelId}
          role="tabpanel"
        >
          <BulkCustomFieldsList
            closeModal={closeModal}
            hasUnsavedChanges={hasUnsavedChanges}
            selectedAssetKeys={selectedAssetKeys}
            setHasUnsavedChanges={setHasUnsavedChanges}
            ugtLocale={ugtLocale as Locale | undefined}
          />
        </div>
      ) : (
        <div
          aria-labelledby={tagsTabId}
          id={tagsTabPanelId}
          role="tabpanel"
        >
          <TagsModalContent
            closeModal={closeModal}
            hasUnsavedChanges={hasUnsavedChanges}
            selectedAssetKeys={selectedAssetKeys}
            setHasUnsavedChanges={setHasUnsavedChanges}
            ugtLocale={ugtLocale}
          />
        </div>
      )}
    </div>
  );
};

const ModalComponent = renderModal(BulkTagWrapper, 'BulkTagWrapper');
export default ModalComponent;
